/**
 * Утилиты для валидации файлов перед загрузкой
 * Проверяет размер, лимиты, количество файлов
 * Автор: AI Market Hub
 */

// Константы лимитов
export const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB (лимит на одно видео)
export const MAX_DURATION_SECONDS = 35 * 60; // 35 минут (2100 секунд)
export const MAX_STORAGE_VIDEOS = 3; // Максимум 3 видео в хранилище
export const MAX_DAILY_UPLOADS = 3; // Максимум 3 загрузки в день
export const LARGE_FILE_WARNING_SIZE = 500 * 1024 * 1024; // 500MB - предупреждение

/**
 * Форматирует размер файла в читаемый формат
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

/**
 * Валидирует размер файла
 * @param {File} file - Файл для проверки
 * @returns {{valid: boolean, error?: string}}
 */
export function validateFileSize(file) {
  if (file.size > MAX_FILE_SIZE) {
    return {
      valid: false,
      error: `Файл слишком большой (${formatFileSize(file.size)}). Максимальный размер: ${formatFileSize(MAX_FILE_SIZE)}`
    };
  }
  return { valid: true };
}

/**
 * Валидирует количество файлов относительно лимита хранения
 * @param {File[]} files - Массив файлов для загрузки
 * @param {number} currentVideoCount - Текущее количество видео в хранилище
 * @returns {{valid: boolean, error?: string, warning?: string}}
 */
export function validateStorageLimit(files, currentVideoCount) {
  const remainingSlots = MAX_STORAGE_VIDEOS - currentVideoCount;
  
  if (currentVideoCount >= MAX_STORAGE_VIDEOS) {
    return {
      valid: false,
      error: `Достигнут лимит хранения (${MAX_STORAGE_VIDEOS} видео). Удалите старые видео для загрузки новых.`
    };
  }
  
  if (files.length > remainingSlots) {
    return {
      valid: false,
      error: `Вы можете загрузить только ${remainingSlots} видео(ов). У вас уже загружено ${currentVideoCount} из ${MAX_STORAGE_VIDEOS}.`
    };
  }
  
  return { valid: true };
}

/**
 * Валидирует дневной лимит загрузки
 * @param {File[]} files - Массив файлов для загрузки
 * @param {number} remainingUploads - Оставшееся количество загрузок сегодня
 * @returns {{valid: boolean, error?: string}}
 */
export function validateDailyLimit(files, remainingUploads) {
  if (remainingUploads === 0) {
    return {
      valid: false,
      error: `Достигнут дневной лимит загрузки (${MAX_DAILY_UPLOADS} видео в день). Попробуйте завтра.`
    };
  }
  
  if (files.length > remainingUploads) {
    return {
      valid: false,
      error: `Вы можете загрузить только ${remainingUploads} видео(ов) сегодня. Осталось ${remainingUploads} из ${MAX_DAILY_UPLOADS}.`
    };
  }
  
  return { valid: true };
}

/**
 * Проверяет, нужно ли показать предупреждение для больших файлов
 * @param {File} file - Файл для проверки
 * @returns {{warning: boolean, message?: string}}
 */
export function checkLargeFileWarning(file) {
  if (file.size > LARGE_FILE_WARNING_SIZE) {
    return {
      warning: true,
      message: `Внимание: файл большой (${formatFileSize(file.size)}). Загрузка может занять некоторое время.`
    };
  }
  return { warning: false };
}

/**
 * Комплексная валидация файлов перед загрузкой
 * @param {File[]} files - Массив файлов для загрузки
 * @param {Object} limits - Объект с лимитами пользователя
 * @param {number} currentVideoCount - Текущее количество видео в хранилище
 * @returns {{valid: boolean, errors: string[], warnings: string[]}}
 */
export function validateFiles(files, limits, currentVideoCount) {
  const errors = [];
  const warnings = [];
  
  if (files.length === 0) {
    return { valid: false, errors: ['Не выбрано ни одного файла'], warnings: [] };
  }
  
  // Проверка дневного лимита
  const dailyLimitCheck = validateDailyLimit(files, limits?.upload?.remaining || 0);
  if (!dailyLimitCheck.valid) {
    errors.push(dailyLimitCheck.error);
  }
  
  // Проверка лимита хранения
  const storageLimitCheck = validateStorageLimit(files, currentVideoCount);
  if (!storageLimitCheck.valid) {
    errors.push(storageLimitCheck.error);
  }
  
  // Проверка каждого файла
  files.forEach((file, index) => {
    // Проверка размера
    const sizeCheck = validateFileSize(file);
    if (!sizeCheck.valid) {
      errors.push(`${file.name}: ${sizeCheck.error}`);
    }
    
    // Предупреждение для больших файлов
    const largeFileCheck = checkLargeFileWarning(file);
    if (largeFileCheck.warning) {
      warnings.push(`${file.name}: ${largeFileCheck.message}`);
    }
  });
  
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Получает сохраненный прогресс загрузки из localStorage
 * @param {string} fileId - Уникальный ID файла
 * @returns {Object|null} Прогресс загрузки или null
 */
export function getUploadProgress(fileId) {
  try {
    const saved = localStorage.getItem(`upload_progress_${fileId}`);
    if (saved) {
      const progress = JSON.parse(saved);
      // Проверяем, не устарел ли прогресс (старше 24 часов)
      if (Date.now() - progress.timestamp < 24 * 60 * 60 * 1000) {
        return progress;
      } else {
        // Удаляем устаревший прогресс
        localStorage.removeItem(`upload_progress_${fileId}`);
      }
    }
  } catch (e) {
    console.warn('[UploadValidation] Ошибка чтения прогресса:', e);
  }
  return null;
}

/**
 * Сохраняет прогресс загрузки в localStorage
 * @param {string} fileId - Уникальный ID файла
 * @param {Object} progress - Объект с прогрессом
 */
export function saveUploadProgress(fileId, progress) {
  try {
    localStorage.setItem(`upload_progress_${fileId}`, JSON.stringify({
      ...progress,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.warn('[UploadValidation] Ошибка сохранения прогресса:', e);
  }
}

/**
 * Удаляет сохраненный прогресс загрузки
 * @param {string} fileId - Уникальный ID файла
 */
export function clearUploadProgress(fileId) {
  try {
    localStorage.removeItem(`upload_progress_${fileId}`);
  } catch (e) {
    console.warn('[UploadValidation] Ошибка удаления прогресса:', e);
  }
}

/**
 * Генерирует уникальный ID для файла
 * @param {File} file - Файл
 * @returns {string} Уникальный ID
 */
export function generateFileId(file) {
  // Используем имя файла, размер и дату модификации для создания уникального ID
  return `${file.name}_${file.size}_${file.lastModified}`.replace(/[^a-zA-Z0-9_]/g, '_');
}


